import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, Info, CheckCircle, XCircle, X } from 'lucide-react';
import { ttStatsAPI } from '../services/api';
import { SystemMessage } from '../types/api.types';

interface SystemMessageBannerProps {
  currentLang: 'en' | 'fr' | 'de' | 'it';
}

export const SystemMessageBanner: React.FC<SystemMessageBannerProps> = ({
  currentLang
}) => {
  const [messages, setMessages] = useState<SystemMessage[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await ttStatsAPI.getSystemMessages();
        setMessages(response.messages);
      } catch (error) {
        console.error('Failed to fetch system messages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
    // Refetch every 5 minutes
    const interval = setInterval(fetchMessages, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleDismiss = (id: string) => {
    setDismissedIds(prev => new Set([...Array.from(prev), id]));
    // Store dismissed IDs in localStorage
    const stored = localStorage.getItem('dismissedMessages');
    const parsed = stored ? JSON.parse(stored) : [];
    localStorage.setItem('dismissedMessages', JSON.stringify([...parsed, id]));
  };

  useEffect(() => {
    // Load dismissed messages from localStorage
    const stored = localStorage.getItem('dismissedMessages');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setDismissedIds(new Set(parsed));
      } catch (error) {
        console.error('Failed to parse dismissed messages:', error);
      }
    }
  }, []);

  const getIconForType = (type: string) => {
    switch (type) {
      case 'warning':
        return AlertCircle;
      case 'error':
        return XCircle;
      case 'success':
        return CheckCircle;
      case 'info':
      default:
        return Info;
    }
  };

  const getColorsForType = (type: string) => {
    switch (type) {
      case 'warning':
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/30',
          border: 'border-yellow-200 dark:border-yellow-700',
          text: 'text-yellow-800 dark:text-yellow-200',
          icon: 'text-yellow-600 dark:text-yellow-400'
        };
      case 'error':
        return {
          bg: 'bg-red-50 dark:bg-red-900/30',
          border: 'border-red-200 dark:border-red-700',
          text: 'text-red-800 dark:text-red-200',
          icon: 'text-red-600 dark:text-red-400'
        };
      case 'success':
        return {
          bg: 'bg-green-50 dark:bg-green-900/30',
          border: 'border-green-200 dark:border-green-700',
          text: 'text-green-800 dark:text-green-200',
          icon: 'text-green-600 dark:text-green-400'
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/30',
          border: 'border-blue-200 dark:border-blue-700',
          text: 'text-blue-800 dark:text-blue-200',
          icon: 'text-blue-600 dark:text-blue-400'
        };
    }
  };

  const visibleMessages = messages
    .filter(msg => {
      // Filter out dismissed messages
      if (dismissedIds.has(msg.id)) return false;

      // Filter out messages with minVersionCode (only relevant for mobile apps)
      if (msg.minVersionCode !== undefined) return false;

      return true;
    })
    .sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

  if (loading || visibleMessages.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <AnimatePresence>
          {visibleMessages.map((message, index) => {
            const Icon = getIconForType(message.type);
            const colors = getColorsForType(message.type);
            const title = message.title[currentLang] || message.title.en;
            const text = message.message[currentLang] || message.message.en;

            return (
              <motion.div
                key={message.id}
                className={`mb-3 pointer-events-auto rounded-xl shadow-lg border ${colors.bg} ${colors.border} overflow-hidden transition-colors duration-300`}
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: index * 8, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 ${colors.icon} transition-colors duration-300`}>
                      <Icon className="w-5 h-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-sm font-semibold ${colors.text} mb-1 transition-colors duration-300`}>
                        {title}
                      </h3>
                      <p className={`text-sm ${colors.text} opacity-90 transition-colors duration-300`}>
                        {text}
                      </p>
                    </div>

                    {/* Dismiss Button */}
                    <button
                      onClick={() => handleDismiss(message.id)}
                      className={`flex-shrink-0 p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-colors duration-300 ${colors.icon}`}
                      aria-label="Dismiss message"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Priority Indicator (for high priority messages) */}
                {message.priority === 'high' && (
                  <div className={`h-1 ${colors.icon.replace('text-', 'bg-')} transition-colors duration-300`} />
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};
